// Page-level RBAC matrix ported from frontend-bk/erp/src/data/auth.js PAGE_ACCESS.
// Used by Sidebar to filter visible nav items.
//
// Canonical roles (CLAUDE.md): admin, director, manager, coordinator,
// accounting, sale, staff. The "sale" role is new — bk modelled sale as
// `staff` + department=sale, so we reuse `staff_sale` access list.
//
// `coordinator` (Điều Phối) ĐÃ KHOÁ 23/08/2026 theo quyết định 0050: máy chủ
// trả 0 quyền cho vai này (PermissionsForRole("coordinator") rỗng) và trưởng
// cửa hàng (`branch_manager`) tiếp quản. Khoá vẫn còn trong bảng dưới nhưng
// RỖNG — xem lý do ở ngay chỗ khai báo.
import { isActiveShiftLead } from "./shift-lead";
import type { Staff } from "@/lib/authv2/store";

// Xuất ra vì rào phòng ban của màn Báo giá doanh nghiệp (QĐ 0178) soi
// CÙNG tập này — khai lần hai là hai danh sách rồi lệch nhau.
export const SALE_DEPTS = new Set(["sales", "sale"]);
const MKT_DEPTS = new Set(["marketing"]);
const RETOUCH_DEPTS = new Set(["retouch"]);
const CSKH_DEPTS = new Set(["cskh"]);
const RECEPTION_DEPTS = new Set(["reception"]);
const MAKEUP_DEPTS = new Set(["makeup"]);
const ASSISTANT_DEPTS = new Set(["studio_assistant"]);
/**
 * Khối ảnh, GỒM cả mã gộp "photo".
 *
 * Mã gộp là mã thật trong hồ sơ nhân sự, không riêng của leader — thợ ảnh
 * thường cũng mang. Bỏ nó ra khỏi đây là accessKey() trả "staff" trần và người
 * đó mất ba trang của nghề mình.
 */
const PHOTO_DEPTS = new Set(["photo", "photo_idphoto", "photo_profile", "photo_corporate"]);

const PAGE_ACCESS: Record<string, string[]> = {
  // "customer_chats" có hai mục, mở cho hai nhóm khác nhau:
  //   · Mục "Khách hàng" (quyết định 0062, 30/08/2026): quản trị + giám đốc
  //     thấy mọi khách; trưởng cửa hàng thấy khách của cơ sở mình; lễ tân
  //     thấy khách ảnh thẻ; CSKH thấy khách profile. Máy chủ cắt phạm vi —
  //     bảng này chỉ mở CỬA, không quyết ai thấy gì.
  //   · Mục "Người giới thiệu & đối tác" (action affiliate.chat, kế hoạch E
  //     — BUSINESS_RULES §23.5): director/accounting/manager_accounting.
  // Kế toán chỉ có mục thứ hai; lễ tân/CSKH chỉ có mục thứ nhất.
  //   · Kinh doanh (QĐ 0275; nới ra cả hộp thư 04/10/2026): mục "Khách
  //     hàng", thấy mọi khách như CSKH. Bốn khoá
  //     staff_sale(s) / manager_sale(s) bên dưới.
  admin: ["set_board", "half_day_slots", "integrations", "dashboard", "director_board", "performance", "orders", "quote_approvals", "store_board", "dispatch_board", "dispatch_points", "dispatch_settings", "customers", "customer_chats", "suggestions", "products", "costumes", "wardrobe_lookup", "schedule", "letan_shifts", "staff_checkins", "staff", "payroll", "reports", "marketing", "businessplan", "rankings", "mgmt_performance", "medals", "regulations", "payment", "cash_drawer", "cash_chain", "retouch", "retouch_print", "cskh_retouch", "cskh_upsale", "cskh_manage", "photo_work", "concept_guides", "quality", "salary_rules", "salary_rules_v2", "salary_analytics", "expenses", "zalo", "ekip_config", "ekip_pool", "ekip_shifts", "promo", "complaints", "affiliate", "branch_kpi", "branch_scores", "qc_overview", "branch_shifts", "qc_board", "general_settings", "obligations", "retouch_dispatch_settings", "print_ops", "print_ops_report", "printer_invoices", "fraud_board"],
  director: ["set_board", "half_day_slots", "dashboard", "director_board", "performance", "orders", "quote_approvals", "store_board", "dispatch_board", "dispatch_points", "dispatch_settings", "customers", "customer_chats", "suggestions", "products", "costumes", "wardrobe_lookup", "schedule", "letan_shifts", "staff_checkins", "staff", "payroll", "reports", "marketing", "businessplan", "rankings", "mgmt_performance", "medals", "regulations", "payment", "cash_drawer", "cash_chain", "retouch", "retouch_print", "cskh_retouch", "cskh_upsale", "cskh_manage", "photo_work", "concept_guides", "quality", "salary_rules", "salary_rules_v2", "salary_analytics", "expenses", "zalo", "ekip_config", "ekip_pool", "ekip_shifts", "promo", "complaints", "affiliate", "branch_kpi", "branch_scores", "qc_overview", "branch_shifts", "qc_board", "obligations", "retouch_dispatch_settings", "print_ops", "print_ops_report", "printer_invoices", "general_settings", "fraud_board"],
  // "promo": quản trị/giám đốc sửa được (promo.write); trưởng Sale và trưởng
  // Marketing CHỈ XEM (promo.read, cấp 23/08/2026) — đó là chỗ họ đọc số
  // liệu hiệu quả của mã. Trang /promos tự gác lại bằng canReadPromos, nên
  // hai lớp phải khớp nhau: mở ở đây mà quên quyền là vào trang gặp "Bạn
  // không có quyền xem"; cấp quyền mà quên mở ở đây là menu mất tăm.
  //
  // VAI ĐIỀU PHỐI ĐÃ KHOÁ (quyết định 0050, 23/08/2026): máy chủ rút sạch
  // quyền của vai này, trưởng cửa hàng tiếp quản việc điều hành cơ sở. Danh
  // sách trang vì thế RỖNG.
  // ⚠️ PHẢI GIỮ KHOÁ, KHÔNG ĐƯỢC XOÁ DÒNG NÀY: `hasAccess` chốt bằng
  // `PAGE_ACCESS[key] ?? PAGE_ACCESS.staff`, mà `??` chỉ nhảy khi `undefined`
  // — xoá khoá đi là vai này rơi về đúng danh sách của nhân viên thường
  // (dashboard, orders, schedule…), tức mở lại cửa thay vì đóng.
  coordinator: [],
  // 3 vai chuỗi (decision 0036, 20/08/2026). branch_manager nắm trọn vận hành
  // một cơ sở + "expenses", BỎ retouch/retouch_print (hàng đợi phòng hậu kỳ
  // tập trung — trưởng cửa hàng theo dõi trạng thái đơn qua Orders, vào trang
  // retouch chỉ gặp workflow không phải của mình). Dữ liệu mọi trang bị
  // BranchScope cắt về 1 chi nhánh ở backend.
  //
  // "qc_board" mở cho vai này từ 14/09/2026 (quyết định 0134). Spec
  // 2026-08-23 §6.3 ban đầu chỉ giao trang cho QC + ban giám đốc, nên
  // `page-access.test.ts` từng ghim "trưởng cửa hàng KHÔNG thấy" — bài kiểm
  // đó đã đảo chiều cùng quyết định, đừng khôi phục theo spec cũ. Họ vốn đã
  // có `review.read` (§5.5) lẫn `incident.write`, tức đã ghi lỗi được cho
  // người của tiệm qua màn Ca vận hành; thiếu đúng cái bảng gộp điểm + lỗi
  // theo từng người. Rào NGHỀ (lễ tân · thợ ảnh · trang điểm) nằm ở MÁY CHỦ
  // — `branchshift/subject_dept.go`, không phải ở bảng này.
  branch_manager: ["set_board", "half_day_slots", "dashboard", "performance", "orders", "store_board", "dispatch_board", "dispatch_points", "dispatch_settings", "customers", "customer_chats", "costumes", "wardrobe_lookup", "products", "schedule", "letan_shifts", "staff_checkins", "ekip_shifts", "ekip_pool", "staff", "payroll", "reports", "rankings", "mgmt_performance", "medals", "regulations", "payment", "cash_drawer", "cash_chain", "cskh_retouch", "cskh_upsale", "cskh_manage", "photo_work", "concept_guides", "quality", "salary_analytics", "expenses", "complaints", "branch_kpi", "branch_scores", "branch_shifts", "qc_board", "obligations", "print_ops", "print_ops_report"],
  // shift_lead — vai Trưởng ca (quyết định 0209) ĐÃ KHOÁ 27/09/2026 (CEO chốt
  // 1a·2a): 0 trang, như `coordinator`. Quyền trưởng ca nay đi theo CA — người
  // được xếp làm ca trưởng ở màn Ca vận hành giữ nguyên vai của mình, và trong
  // giờ ca ± 2 tiếng mở được Bảng suất qua nhánh `isActiveShiftLead` trong
  // `hasAccess`. Mã vai vẫn phải nằm trong `accessKey` (xem chú thích ở đó).
  shift_lead: [],
  // area_manager chỉ đọc/giám sát cụm (điểm ngỏ spec §6, đã nới ở dưới);
  // cash_chain là trang ĐỌC sức khoẻ két nên giữ.
  // "payment" mở từ QĐ 0167 Q4 (CEO 16/09/2026): quản lý cụm đọc sổ thu tiền
  // của cụm mình — nút thu tiền vẫn theo quyền `payment.add`.
  // "cash_drawer" mở từ rà soát két 10/10/2026 (CEO duyệt 5a): quản lý cụm là
  // người KÝ két mà trước đó mở trang là "Bạn không có quyền xem". Chỉ để ký —
  // nhận két, đếm két vẫn theo `cashdrawer.operate`, vai này không có.
  // "payroll" (phiếu lương của chính mình) và "expenses" (QĐ 0132 §8 câu 4a —
  // máy chủ đã cho đọc) thêm 17/10/2026, kiểm thử đóng vai L2-09/L2-10.
  // "staff_checkins" mở 26/10/2026 (QĐ 0464): CHỈ XEM chấm công các cơ sở trong
  // cụm — máy chủ cấp `staff_checkin.read`, không cấp hai quyền sửa giờ.
  // "retouch_analytics" mở 28/10/2026 (CEO chốt 7a): chỉ thẻ "Theo dõi" của màn
  // Hậu kỳ — đơn cơ sở trong cụm, không tiền (máy chủ cắt ở retouchanalytics).
  // KHÔNG cấp "retouch": bảng đó lái chuông @ ghi chú ảnh vào bàn sửa ảnh.
  area_manager: ["set_board", "half_day_slots", "dashboard", "director_board", "performance", "orders", "customer_chats", "customers", "schedule", "ekip_shifts", "staff", "staff_checkins", "payroll", "expenses", "reports", "rankings", "mgmt_performance", "medals", "regulations", "payment", "cash_drawer", "cash_chain", "complaints", "branch_kpi", "branch_scores", "qc_overview", "branch_shifts", "obligations", "print_ops", "dispatch_points", "retouch_analytics", "wardrobe_lookup"],
  // qc: khoá HẸP, chỉ dùng cho QC CHƯA gán phòng — đọc vận hành, tuyệt đối
  // không trang tiền/lương/khách. QC có phòng đi khoá `qc_<phòng>` sinh ở cuối
  // file, rộng hơn hẳn (kế thừa leader phòng mình).
  // "quality" cố ý vẫn vắng ở khoá hẹp này: QC chưa gán phòng không phụ trách
  // đội nào để mà chấm. Khoá `qc_<phòng>` CÓ "quality", và cổng nội dung của
  // trang đó nay nhận `review.read` chứ không còn đòi `payroll.read`
  // (spec 2026-08-23 §5.7).
  // "dispatch_board" (Bảng suất cơ sở, quyết định 0077) có mặt ở đây và ở
  // manager_photo* / manager_makeup vì backend cấp `dispatch.read` cho CẢ
  // RoleQC lẫn RoleManager (`actions.go`) — QC soi ê-kíp thì phải nhìn được
  // suất của ê-kíp đó. Bảng chỉ ĐỌC với họ: nút đổi gán đi theo
  // `dispatch.override`, và vai QC cố ý KHÔNG có action đó.
  // Khoá `qc_photo` / `qc_makeup` / `qc_reception` nhận trang này TỰ ĐỘNG qua
  // `qcPagesOf` ở cuối file, không cần gõ tay.
  // "quality" (Đánh Giá — chấm sao ê-kíp theo đơn) thêm 02/09/2026. Bốn khoá
  // `qc_*` nhận trang này sẵn vì kế thừa từ `manager_*`; khoá `qc` hẹp thì
  // không, nên QC phòng chưa có khoá riêng (phòng CSKH) mất đúng cái màn hình
  // trung tâm của việc mình. Cổng nội dung `canReadQuality` vốn đã cho vai QC
  // qua — chỉ bảng cửa này chặn, và chặn im lặng: menu không hiện, không lỗi.
  qc: ["set_board", "dashboard", "performance", "orders", "customer_chats", "schedule", "dispatch_board", "ekip_shifts", "staff", "staff_checkins", "rankings", "medals", "regulations", "costumes", "wardrobe_lookup", "concept_guides", "complaints", "branch_kpi", "branch_scores", "qc_overview", "branch_shifts", "quality", "qc_board", "qc_desk", "qc_journey"],
  // "payroll", "salary_analytics", "salary_rules" (CEO chốt 17/10/2026 "kế toán
  // trưởng: cả hai đủ công cụ"): vai Kế toán là người làm lương (payroll
  // salaryScope "all", QĐ 0035) mà trước đây menu không có Bảng lương.
  accounting: ["dashboard", "performance", "orders", "customer_chats", "payment", "cash_drawer", "cash_chain", "reports", "staff", "staff_checkins", "payroll", "salary_analytics", "salary_rules", "rankings", "regulations", "salary_rules_v2", "expenses", "complaints", "affiliate", "branch_kpi", "branch_shifts", "obligations", "print_ops", "printer_invoices"],
  // "printer_invoices" (CEO chốt 17/10/2026): trưởng phòng Kế toán làm Đối soát
  // nhà in như vai Kế toán (máy chủ cấp qua grantObligationReadToChiefAccountant).
  manager_accounting: ["dashboard", "performance", "orders", "customer_chats", "payment", "cash_drawer", "cash_chain", "reports", "staff", "staff_checkins", "payroll", "rankings", "regulations", "quality", "salary_analytics", "expenses", "complaints", "branch_kpi", "branch_shifts", "obligations", "printer_invoices"],
  manager_sales: ["half_day_slots", "dashboard", "performance", "orders", "quote_approvals", "customers", "customer_chats", "suggestions", "products", "costumes", "wardrobe_lookup", "staff", "staff_checkins", "payroll", "schedule", "ekip_shifts", "reports", "rankings", "medals", "regulations", "payment", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts", "promo"],
  manager_sale: ["half_day_slots", "dashboard", "performance", "orders", "quote_approvals", "customers", "customer_chats", "suggestions", "products", "costumes", "wardrobe_lookup", "staff", "staff_checkins", "payroll", "schedule", "ekip_shifts", "reports", "rankings", "medals", "regulations", "payment", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts", "promo"],
  // "complaints" (QĐ 0204): quản lý marketing vào trang Khiếu nại để ghi SỔ
  // TIẾNG KHÁCH — họ là người trông fanpage và Google, nơi phốt công khai rơi
  // xuống. Thẻ "Phiếu khiếu nại" của trang đó vẫn gác riêng, nên họ ghi được sổ
  // mà không mở được hàng khiếu nại đang xử của phòng CSKH.
  manager_marketing: ["dashboard", "performance", "orders", "staff", "staff_checkins", "payroll", "schedule", "marketing", "reports", "rankings", "medals", "regulations", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts", "promo"],
  // "customers" (QĐ 0356, CEO chốt 12/10/2026): lễ tân xem cả danh bạ khách và
  // thêm khách mới. QC lễ tân nhận trang này qua qcPagesOf("manager_reception").
  manager_reception: ["set_board", "half_day_slots", "dashboard", "performance", "orders", "customers", "customer_chats", "store_board", "dispatch_board", "dispatch_points", "costumes", "wardrobe_lookup", "staff", "payroll", "schedule", "letan_shifts", "staff_checkins", "rankings", "medals", "regulations", "payment", "cash_drawer", "cash_chain", "cskh_upsale", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts", "print_ops"],
  // 23/08/2026: khối ảnh dùng "photo_work" (trang Sửa ảnh — bàn làm việc của
  // thợ, mở thẳng thẻ Sửa ảnh của đơn) thay cho "retouch" (hàng đợi hậu kỳ).
  // Phòng retouch giữ "retouch". Leader khối ảnh vẫn giữ "retouch_print".
  manager_photo: ["set_board", "dashboard", "performance", "orders", "costumes", "wardrobe_lookup", "staff", "staff_checkins", "payroll", "schedule", "dispatch_board", "dispatch_points", "my_dispatch", "ekip_pool", "ekip_shifts", "rankings", "medals", "regulations", "retouch_print", "photo_work", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts"],
  manager_photo_idphoto: ["set_board", "dashboard", "performance", "orders", "staff", "staff_checkins", "payroll", "schedule", "dispatch_board", "dispatch_points", "my_dispatch", "ekip_pool", "ekip_shifts", "rankings", "medals", "regulations", "photo_work", "costumes", "wardrobe_lookup", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts"],
  manager_photo_profile: ["set_board", "dashboard", "performance", "orders", "staff", "staff_checkins", "payroll", "schedule", "dispatch_board", "dispatch_points", "my_dispatch", "ekip_pool", "ekip_shifts", "rankings", "medals", "regulations", "retouch_print", "photo_work", "costumes", "wardrobe_lookup", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts"],
  // Khoá này TRƯỚC ĐÂY KHÔNG TỒN TẠI: `accessKey()` sinh `manager_${dept}` cho
  // mọi manager, và `hasAccess` rơi về `PAGE_ACCESS.staff` khi khoá vắng — nên
  // leader ảnh doanh nghiệp đang chạy bằng quyền nhân viên thường, thiếu cả
  // `ekip_pool`, `payroll` và `salary_analytics` so với leader ảnh khác. Khai
  // sao y `manager_photo_idphoto`. Đây là sửa lỗ hổng quyền sẵn có, nằm ngoài
  // yêu cầu ban đầu của trang mới — nêu rõ khi review.
  manager_photo_corporate: ["set_board", "dashboard", "performance", "orders", "staff", "staff_checkins", "payroll", "schedule", "dispatch_board", "dispatch_points", "my_dispatch", "ekip_pool", "ekip_shifts", "rankings", "medals", "regulations", "photo_work", "costumes", "wardrobe_lookup", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts"],
  manager_makeup: ["set_board", "dashboard", "performance", "orders", "staff", "staff_checkins", "payroll", "schedule", "dispatch_board", "dispatch_points", "my_dispatch", "ekip_pool", "ekip_shifts", "rankings", "medals", "regulations", "costumes", "wardrobe_lookup", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts"],
  // "payment" rút khỏi trưởng phòng hậu kỳ theo QĐ 0167 Q4: sổ thu tiền không
  // mở cho trưởng phòng ngoài kế toán / lễ tân / sale / CSKH (máy chủ chặn).
  manager_retouch: ["dashboard", "performance", "orders", "customer_chats", "staff", "staff_checkins", "payroll", "schedule", "rankings", "medals", "regulations", "retouch", "retouch_print", "costumes", "wardrobe_lookup", "concept_guides", "quality", "salary_analytics", "complaints", "branch_kpi", "branch_shifts", "retouch_dispatch_settings", "print_ops"],
  // "ekip_shifts" mở cho CSKH từ 16/09/2026 (Câu 22b): phòng này nay đăng ký
  // ca tuần như sale, và bốn chỉ số KPI theo đơn quy việc về người TRỰC CA —
  // không mở cửa ở đây thì menu mất tăm, không ai đăng ký được, và bốn chỉ số
  // rơi hết về bậc ngã về. Máy chủ vốn đã cấp ekipshift.* cho vai staff và
  // manager, nên đây là lớp duy nhất còn chặn.
  manager_cskh: ["dashboard", "performance", "orders", "customers", "customer_chats", "suggestions", "staff", "staff_checkins", "payroll", "schedule", "ekip_shifts", "payment", "reports", "rankings", "medals", "regulations", "retouch_print", "cskh_retouch", "cskh_upsale", "cskh_manage", "quality", "salary_analytics", "zalo", "complaints", "branch_kpi", "branch_shifts", "print_ops", "wardrobe_lookup"],
  // "payment" mở cho nhân sự CSKH theo QĐ 0167 Q4 (CEO thêm CSKH vào sổ thu tiền).
  staff_cskh: ["dashboard", "performance", "orders", "customers", "customer_chats", "suggestions", "schedule", "ekip_shifts", "retouch_print", "cskh_retouch", "cskh_upsale", "payment", "payroll", "staff", "rankings", "medals", "regulations", "quality", "complaints", "branch_kpi", "branch_shifts", "print_ops", "wardrobe_lookup"],
  staff_marketing: ["dashboard", "performance", "orders", "marketing", "schedule", "payroll", "staff", "rankings", "medals", "regulations", "quality", "complaints", "branch_kpi", "branch_shifts"],
  // "products" (quyết định 7a, CEO 09/10/2026): sale xem Bảng giá CHỈ ĐỌC — mọi
  // nút ghi trên trang gác bằng catalog.write, sale không có quyền đó.
  staff_sales: ["half_day_slots", "dashboard", "performance", "orders", "customers", "customer_chats", "suggestions", "products", "costumes", "wardrobe_lookup", "schedule", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "payment", "quality", "complaints", "branch_kpi", "branch_shifts"],
  staff_sale: ["half_day_slots", "dashboard", "performance", "orders", "customers", "customer_chats", "suggestions", "products", "costumes", "wardrobe_lookup", "schedule", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "payment", "quality", "complaints", "branch_kpi", "branch_shifts"],
  // Thêm "customer_chats" cho mục chat đối tác (máy chủ mở theo phòng Kế
  // toán, kiểm thử 17/10/2026 M04). Bỏ "customers" (kiểm thử đóng vai 17/10/2026, L2-15): máy chủ không cấp
  // crm.read cho nhân viên kế toán, khoá chép từ hệ cũ chỉ làm hiện thẻ "Gợi ý
  // cho khách" ở /products luôn báo lỗi giả.
  staff_accounting: ["dashboard", "performance", "orders", "payment", "customer_chats", "payroll", "staff", "rankings", "regulations", "complaints", "branch_kpi", "branch_shifts"],
  staff_retouch: ["dashboard", "performance", "orders", "customer_chats", "retouch", "payroll", "staff", "rankings", "medals", "regulations", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts", "print_ops"],
  staff_makeup: ["set_board", "dashboard", "performance", "orders", "schedule", "my_dispatch", "ekip_pool", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts"],
  staff_reception: ["set_board", "half_day_slots", "dashboard", "performance", "orders", "customers", "customer_chats", "store_board", "dispatch_board", "costumes", "wardrobe_lookup", "schedule", "letan_shifts", "cskh_upsale", "payroll", "staff", "rankings", "medals", "regulations", "payment", "cash_drawer", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts", "print_ops"],
  // Gộp 24/08: demo-thangdz vừa mở "Sửa hình" (retouch) cho thợ ảnh thẻ
  // (9c005d9e) vì họ không có chỗ nào nộp bản sửa; profile đã có từ trước,
  // corporate thì sót. Spec/quyết định 0049 giải đúng nhu cầu đó bằng trang
  // riêng "Sửa ảnh" (photo_work) — đủ CẢ BA phòng ảnh và nộp bản sửa ngay
  // trong thẻ — nên giữ photo_work và KHÔNG cộng thêm retouch: hàng đợi Sửa
  // hình là bàn làm việc của phòng hậu kỳ, mở cho thợ ảnh là trả lại đúng
  // cảnh lộn sân mà trang mới sinh ra để dẹp. Năng lực không mất, chỉ đổi cửa.
  staff_photo_idphoto: ["set_board", "dashboard", "performance", "orders", "schedule", "my_dispatch", "ekip_pool", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "photo_work", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts"],
  staff_photo_profile: ["set_board", "dashboard", "performance", "orders", "schedule", "my_dispatch", "ekip_pool", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "photo_work", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts"],
  staff_photo_corporate: ["set_board", "dashboard", "performance", "orders", "schedule", "my_dispatch", "ekip_pool", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "photo_work", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts"],
  // Mã phòng GỘP "photo" — không phải chỉ leader mới mang. Hồ sơ nhân sự cũ
  // lưu mã gộp này cho cả thợ ảnh thường (xem fixture staff bảng `staff` ở
  // backend/internal/core/ekipclaim/integration_test.go: PAT01 role=staff
  // department='photo'), nên thiếu khoá này là accessKey() rơi về "staff"
  // trần và người đó mất Đăng ký ca, Nhận đơn lẫn Buổi chụp — ba trang của
  // chính nghề mình. Khoá manager_photo đã có từ trước; đây là vế còn thiếu.
  staff_photo: ["set_board", "dashboard", "performance", "orders", "schedule", "my_dispatch", "ekip_pool", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "photo_work", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts"],
  // Trợ lý Studio (QĐ 0164). Danh sách nền `staff` bên dưới CỘNG "ekip_shifts",
  // TRỪ "orders".
  //
  // 🔴 Trừ "orders" là chủ ý, không phải sót: `order/visibility.go` không khai
  // phòng này nên máy chủ trả VisibilityNone — mở menu là mời họ vào một danh
  // sách rỗng. Rỗng không ai đi báo, họ chỉ tưởng hôm nay chưa có đơn. Trợ lý
  // biết mình làm gì qua Lịch chụp (cả cơ sở, không kèm tiền và số điện thoại
  // — QĐ 0164), không qua sổ đơn.
  //
  // Bốn mục của ê-kíp nhận khách — ekip_pool, my_dispatch, dispatch_board,
  // photo_work — cũng vắng: trợ lý chưa được máy chia đơn xếp suất. Đó là
  // ĐỢT 2 (`orders.assistants`); mở sớm ở đây là bày ra bốn trang rỗng.
  staff_studio_assistant: ["set_board", "dashboard", "performance", "schedule", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts"],
  // Hôm nay CHƯA có ai mang vai này — ca trợ lý do trưởng cửa hàng ký (QĐ
  // 0164), không do một trưởng phòng riêng. Khoá vẫn phải khai: `accessKey()`
  // sinh `manager_${dept}` cho mọi manager mà không qua danh sách nào, và
  // `hasAccess` rơi về `PAGE_ACCESS.staff` khi khoá vắng — tức nhân sự gán
  // nhầm một vai trưởng phòng là người đó được MỞ THÊM trang Đơn hàng chứ
  // không phải bị chặn. Đúng cái bẫy đã bắt `manager_photo_corporate`.
  //
  // Bằng danh sách nhân viên + "salary_analytics" (khuôn chung của mọi khoá
  // manager_*). KHÔNG thêm quyền duyệt ca ở đây — đó là việc của
  // `canApproveDept`, và nó từ chối phòng này.
  manager_studio_assistant: ["set_board", "dashboard", "performance", "schedule", "ekip_shifts", "payroll", "staff", "rankings", "medals", "regulations", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts", "salary_analytics"],
  staff: ["dashboard", "performance", "orders", "schedule", "payroll", "staff", "rankings", "medals", "regulations", "costumes", "wardrobe_lookup", "concept_guides", "quality", "complaints", "branch_kpi", "branch_shifts"],
  // "branch_kpi" có mặt ở MỌI khoá trên (trừ viewer) là cố ý, hai lý do:
  // accessKey() trả manager_<phòng> / staff_<phòng> chứ không có khoá gộp, mà
  // chưa biết CEO xếp trưởng chi nhánh vào phòng nào — thiếu đúng khoá đó là
  // họ đăng nhập vào không thấy menu; và quy chế KPI 19/08/2026 chia 50% quỹ
  // vượt cho đội, nên nhân viên tiệm phải xem được tiệm mình đang đạt bao
  // nhiêu. Ranh giới thật nằm ở backend: BranchScope cắt dữ liệu nên ai mở
  // trang cũng chỉ thấy số chi nhánh mình được gán, và thẻ "Giao KPI" chỉ
  // hiện với người có kpi.target.write.
  //
  // Read-only archetype (decision 0010). Nav surface is intentionally narrow and
  // excludes sensitive pages (payroll, salary, payment, customers). Write actions
  // are blocked at the permission layer regardless of what nav is shown.
  // "reports" rời vai Chỉ xem 17/10/2026 (CEO chốt Q14): vai này không xem doanh thu,
  // máy chủ đóng /dashboard/report-analytics và /reports/brand-revenue với nó.
  viewer: ["dashboard", "performance", "orders", "schedule", "rankings", "medals", "regulations", "costumes", "concept_guides", "quality"],
};
// QC = leader phòng mình trừ tiền/lương (spec 2026-08-23 §5.7): khoá
// `qc_<phòng>` SINH từ `manager_<phòng>` — bỏ trang tiền/lương, thêm bảng
// "Chất lượng nhân sự" (qc_board; nav item lên ở Đợt 2, khoá cấp trước để hai
// đợt không lệch ma trận). Sinh thay vì chép tay: leader được thêm trang nào,
// QC theo luôn — trừ khi trang đó là tiền.
// "customer_chats" nằm đây vì QC kế thừa danh sách trang của leader phòng
// mình, mà quyết định 0062 chỉ giao chat khách cho trưởng cửa hàng, lễ tân và
// CSKH — không giao cho QC. Không loại ra thì QC phòng lễ tân thấy menu rồi
// bấm vào gặp danh sách rỗng (máy chủ trả đúng: họ không có customer_chat.read).
// "payroll" rời danh sách này 17/10/2026 (kiểm thử đóng vai L2-09): QC không
// mở được phiếu lương của CHÍNH MÌNH. Bảng lương tự cắt về "Lương của tôi"
// theo salaryScope (vai qc → "self"), nên mở trang không lộ lương ai khác.
const QC_EXCLUDED_PAGES = new Set([
  "salary_analytics",
  "payment",
  "cash_drawer",
  "cash_chain",
  "print_ops",
]);

function qcPagesOf(leaderKey: string): string[] {
  const base = PAGE_ACCESS[leaderKey] ?? [];
  // "customer_chats" cấp cho MỌI QC bất kể phòng (CEO chốt 24/09/2026): phạm
  // vi chat của họ là miền được gán, không phải nghề của phòng họ xuất thân —
  // nên nó không được phụ thuộc vào việc leader phòng ấy có trang này hay
  // không. Trước 24/09 trang này nằm trong QC_EXCLUDED_PAGES.
  // "branch_scores" (QĐ 0246, 28/09/2026): QC mọi phòng xem điểm cơ sở mình
  // được giao — đó là kết quả trực tiếp của lượt Kiểm cơ sở họ chấm.
  // "qc_overview" (QĐ 0297, 05/10/2026, CEO chốt 3b): Bảng QC mở cho QC mọi
  // phòng — mỗi người thấy phần mình và phần đồng nghiệp.
  // "qc_desk" (QĐ 0521, 03/11/2026): Bàn làm việc QC, mọi phòng.
  return [...new Set([...base.filter((p) => !QC_EXCLUDED_PAGES.has(p)), "qc_board", "customer_chats", "branch_scores", "qc_overview", "qc_desk"])];
}

// "Làm file & gửi in" là việc làm file, không phải việc chấm của QC (CEO 1a · 9a,
// 02/11/2026) — hai khoá QC bỏ riêng, trưởng phòng ảnh và hậu kỳ giữ.
PAGE_ACCESS.qc_photo = qcPagesOf("manager_photo").filter((p) => p !== "retouch_print");
PAGE_ACCESS.qc_makeup = qcPagesOf("manager_makeup");
PAGE_ACCESS.qc_reception = qcPagesOf("manager_reception");
PAGE_ACCESS.qc_retouch = qcPagesOf("manager_retouch").filter((p) => p !== "retouch_print");
// Mục menu "Hậu kiểm ảnh gốc" (QĐ 0521): QC sửa hình (CEO chốt 2a) và QC chụp
// ảnh (vòng 3 · 3a); QC chụp ảnh có thêm mục "Ảnh thẻ".
PAGE_ACCESS.qc_retouch.push("qc_raw_review");
PAGE_ACCESS.qc_photo.push("qc_raw_review", "qc_idphoto");

// Hai cửa mới của khung hiệu suất (CEO chốt 01/09/2026): "Chấm điểm"
// (performance_review) và "Điều hành khung" (performance_admin). Mở cho ĐÚNG
// những khoá đã có "performance" — tức mọi vai trừ `coordinator` (đã khoá).
//
// SINH chứ không chép tay vào 35 dòng: ba cửa là ba mặt của MỘT khung, nên
// chúng phải mở cùng nhau hoặc đóng cùng nhau. Chép tay là sớm muộn thêm vai
// mới mà quên một dòng, rồi người đó thấy cửa này mất cửa kia mà không ai
// hiểu tại sao.
//
// Bảng này chỉ mở CỬA. Ai thực sự thấy mục nào do `nav-content-gates` quyết
// bằng quyền thật (canOpenReviewDesk / canOpenFrameworkDesk), và máy chủ gác
// lại lần nữa ở từng đường dữ liệu.
for (const pages of Object.values(PAGE_ACCESS)) {
  if (pages.includes("performance")) {
    pages.push("performance_review", "performance_admin");
  }
}

// "Đồ quá hạn" (CEO chốt 2a, 29/09/2026): cùng tập vai máy chủ cấp
// `equipment_loan.overdue.read` — quản trị, giám đốc, trưởng cửa hàng, quản lý
// cụm và MỌI trưởng phòng. Sinh theo khoá thay vì chép tay vào từng dòng
// manager_*: thêm phòng mới là trưởng phòng đó tự có. Khoá qc_* đã sinh ở trên
// nên KHÔNG nhận trang này (QC không có quyền). Cổng nội dung
// `canReadOverdueLoans` cắt lại theo quyền thật trong phiên.
for (const [key, pages] of Object.entries(PAGE_ACCESS)) {
  if (["admin", "director", "branch_manager", "area_manager"].includes(key) || key.startsWith("manager_")) {
    pages.push("equipment_overdue");
  }
}

// "Việc cần xem" (QĐ 0428, CEO chốt 1b 20/10/2026): người nhận bản tin AI 22 giờ —
// quản trị, giám đốc, trưởng cửa hàng, quản lý cụm và MỌI trưởng phòng; cùng tập
// vai máy chủ cấp `ai_briefing.read_own`. Khoá qc_* sinh ở trên nên KHÔNG nhận
// (QC không có quyền). Cổng nội dung `canReadAiBriefing` cắt lại theo quyền thật.
for (const [key, pages] of Object.entries(PAGE_ACCESS)) {
  if (["admin", "director", "branch_manager", "area_manager"].includes(key) || key.startsWith("manager_")) {
    pages.push("ai_briefing");
  }
}

// "Kho Ý Tưởng" (QĐ 0445, CEO chốt 3a 22/10/2026): chỉ quản trị + giám đốc — cùng
// tập vai máy chủ cấp `idea_bank.read`. Cổng nội dung `canReadIdeaBank` cắt lại
// theo quyền thật trong phiên.
for (const key of ["admin", "director"]) {
  PAGE_ACCESS[key]?.push("idea_bank");
}


// Mục Kho (kho-erp v1): mọi vai có `kho.read` — tức mọi khoá trừ vai đã khoá
// (coordinator, shift_lead: 0 trang). Máy chủ cắt dữ liệu theo vai; cổng nội
// dung còn đòi công tắc KHO_ERP_ENABLED.
for (const pages of Object.values(PAGE_ACCESS)) {
  if (pages.length > 0) pages.push("kho");
}

// "Mượn / trả đồ" (máy quét trong ERP, CEO chốt 08/10/2026): mọi vai có trang
// đều mượn đồ; quyền vào cơ sở do kho kiểm khi quét tờ mã cửa kho. Cổng nội
// dung còn đòi `kho.command` và hai công tắc.
for (const pages of Object.values(PAGE_ACCESS)) {
  if (pages.length > 0) pages.push("muon_tra_do");
}

// Hộp thư kín "Tố giác & Góp ý" (QĐ 0272). Trang gửi: mọi vai máy chủ cấp
// `sealedinbox.submit` (Câu 7a) — mọi khoá có trang trừ `viewer`; vai đã khoá
// (coordinator, shift_lead) có 0 trang nên tự đứng ngoài. Trang đọc: lớp thô là
// quản trị + giám đốc; cổng nội dung còn đòi có tên trong danh sách người đọc.
for (const [key, pages] of Object.entries(PAGE_ACCESS)) {
  if (pages.length > 0 && key !== "viewer") pages.push("sealed_inbox");
}
// Khoản chi của tôi (QĐ 0283): mọi khoá có trang trừ `viewer` — máy chủ chặn
// viewer và vai đã khoá ở cửa ghi; dữ liệu luôn là khoản của chính người mở.
for (const [key, pages] of Object.entries(PAGE_ACCESS)) {
  if (pages.length > 0 && key !== "viewer") pages.push("my_expense_claims");
}
// Thông báo của tôi (20/10/2026): mọi khoá có trang trừ `viewer` — dữ liệu luôn
// là hộp thư của chính người mở; cổng nội dung còn đòi `notification.read`.
for (const [key, pages] of Object.entries(PAGE_ACCESS)) {
  if (pages.length > 0 && key !== "viewer") pages.push("my_notifications");
}
// Kênh nhân sự (kenh-nhan-su-erp-v1, CEO 1a): mọi khoá có trang trừ `viewer` —
// dữ liệu luôn là của chính người mở; cổng nội dung còn đòi máy chủ bật luồng
// kenh_nhan_su (và có tên trong danh sách mở thử nếu đang mở thử).
for (const [key, pages] of Object.entries(PAGE_ACCESS)) {
  if (pages.length > 0 && key !== "viewer") pages.push("staff_channel");
}
// "Của tôi" (QĐ 0321 · M8) + trang con "Bộ sưu tập" (QĐ 0404): mọi khoá có trang trừ `viewer`. Mọi thẻ chỉ đọc dữ
// liệu của chính người mở; thẻ nào dẫn sang trang khác thì tự hỏi quyền trang đó.
for (const [key, pages] of Object.entries(PAGE_ACCESS)) {
  if (pages.length > 0 && key !== "viewer") pages.push("me", "me_collection");
}
PAGE_ACCESS.admin!.push("sealed_inbox_reader");
PAGE_ACCESS.director!.push("sealed_inbox_reader");
// Phòng máy (QĐ 0541): lớp trang cho quản trị + giám đốc; cổng thật là có tên trong phòng.
PAGE_ACCESS.admin!.push("hunch_room");
PAGE_ACCESS.director!.push("hunch_room");

// "Quản lý album" (QĐ 0301, đợt 1): đúng những ai hôm nay mở được kho ảnh —
// nhân viên và trưởng phòng của bốn mã phòng ảnh, hậu kỳ, CSKH, trang điểm,
// marketing — cộng sale (ảnh hoàn thiện của đơn mình bán) và ba vai điều hành.
// Lễ tân, kế toán, trợ lý studio, quản lý cụm KHÔNG có mục này.
//
// Đặt SAU khối sinh `qc_*` ở trên một cách cố ý: QC không thừa kế trang này từ
// trưởng phòng mình. QC không có tên trong ê-kíp của đơn nên máy chủ trả danh
// sách rỗng — thấy menu rồi bấm vào chỉ gặp trang trống.
//
// Bảng này chỉ mở CỬA. Cổng nội dung `canOpenAlbumManager` cắt lại theo quyền
// thật, và máy chủ quyết từng album (cùng luật với việc mở album đó).
const ALBUM_MANAGER_DEPTS = [
  "photo",
  "photo_idphoto",
  "photo_profile",
  "photo_corporate",
  "retouch",
  "cskh",
  "makeup",
  "marketing",
  "sales",
  "sale",
];
for (const dept of ALBUM_MANAGER_DEPTS) {
  PAGE_ACCESS[`staff_${dept}`]?.push("album_manager");
  PAGE_ACCESS[`manager_${dept}`]?.push("album_manager");
}
for (const key of ["admin", "director", "branch_manager"]) {
  PAGE_ACCESS[key]?.push("album_manager");
}
// Năng suất nhân sự (QĐ 0386): cùng tập vai máy chủ cấp
// `staff_productivity.read` — quản trị, giám đốc, trưởng cửa hàng, cùng trưởng
// phòng và QC của các phòng CÓ sổ việc (ảnh, trang điểm, hậu kỳ, CSKH, lễ tân,
// trợ lý studio). Trưởng phòng kế toán / kinh doanh / marketing KHÔNG có: máy
// chủ trả 403 vì phòng họ chưa có bảng. Đặt SAU khối sinh `qc_*` để bốn khoá QC
// có mặt. Cổng nội dung `canSeeStaffProductivity` cắt lại theo quyền trong vé;
// ai thấy ai do máy chủ quyết.
const STAFF_PRODUCTIVITY_KEYS = [
  "admin",
  "director",
  "branch_manager",
  "manager_photo",
  "manager_photo_idphoto",
  "manager_photo_profile",
  "manager_photo_corporate",
  "manager_makeup",
  "manager_retouch",
  "manager_cskh",
  "manager_reception",
  "manager_studio_assistant",
  "qc_photo",
  "qc_makeup",
  "qc_reception",
  "qc_retouch",
];
for (const key of STAFF_PRODUCTIVITY_KEYS) {
  PAGE_ACCESS[key]?.push("staff_productivity");
}
// Sổ album ngoài đơn (QĐ 0306): quản trị + giám đốc — cùng tập vai mang
// gallery.loose.audit ở máy chủ (auth/actions.go).
for (const key of ["admin", "director"]) {
  PAGE_ACCESS[key]?.push("album_ledger");
}
// Kho ảnh nền (QĐ 0353): ai dùng Xưởng ghép (hậu kỳ, CSKH, sale), marketing (người
// tải ảnh lên) và các vai quản lý — cùng tập vai/phòng của compose.backdrop.read
// ở máy chủ (auth/actions.go + AllowOrDept). Cổng nội dung cắt lại theo quyền thật.
for (const key of Object.keys(PAGE_ACCESS)) {
  if (key.startsWith("manager_")) PAGE_ACCESS[key]?.push("compose_backdrops");
}
for (const key of ["admin", "director", "branch_manager", "staff_marketing", "staff_retouch", "staff_cskh", "staff_sales", "staff_sale"]) {
  if (!PAGE_ACCESS[key]?.includes("compose_backdrops")) PAGE_ACCESS[key]?.push("compose_backdrops");
}
// Phòng Kinh doanh (QĐ 0483): cùng tập người máy chủ cho đọc (`salesorg.CanRead`):
// quản trị, giám đốc, CẢ phòng Kế toán (CEO 22a, 28/10/2026 — kế toán trưởng mang
// vai Trưởng phòng), người phòng sale / CSKH. Ai thấy tiểu đội nào do MÁY CHỦ cắt
// theo chức trong cơ cấu HRM gửi.
for (const key of ["admin", "director", "accounting", "manager_accounting", "staff_accounting", "manager_sale", "manager_sales", "manager_cskh", "staff_sale", "staff_sales", "staff_cskh"]) {
  PAGE_ACCESS[key]?.push("sales_team");
}

function accessKey(staff: Staff): string {
  const role = (staff.role || "").toLowerCase().trim();
  const dept = (staff.department || "").toLowerCase().trim();

  if (role === "cskh" || role === "gnpf-cskh") return "staff_cskh";
  if (role === "reception" || role === "gnpf-reception") return "staff_reception";

  // ⚠️ `shift_lead` PHẢI ở trong danh sách này (quyết định 0209). Vai đó gắn
  // với CA, không gắn với phòng — người mang nó vẫn có một mã phòng trên hồ
  // sơ, nên bỏ ra là nó rơi xuống cuối hàm và nhận khoá `staff_<phòng>`: một
  // trưởng ca mang mã "reception" bỗng thấy trọn nav lễ tân, đúng thứ khoá
  // hẹp sinh ra để chặn. Cùng bẫy với `coordinator` ngay dưới.
  //
  // ⚠️ `coordinator` PHẢI ở lại danh sách này dù vai đã khoá (quyết định
  // 0050): bỏ ra là nó rơi xuống cuối hàm và nhận khoá "staff" — tức quyền
  // nhân viên thường. Khoá thật nằm ở `PAGE_ACCESS.coordinator = []`.
  if (
    role === "admin" || role === "director" || role === "coordinator" ||
    role === "branch_manager" || role === "area_manager" || role === "shift_lead"
  ) {
    return role;
  }

  if (role === "qc") {
    // QC = leader phòng mình: khoá theo phòng như manager_<dept>. Mã khối ảnh
    // gộp `photo` và ba mã cụ thể đều về qc_photo. Chưa gán phòng → khoá `qc`
    // hẹp (chỉ đọc), KHÔNG phải quyền leader của phòng nào.
    if (dept.startsWith("photo")) return "qc_photo";
    if (dept === "makeup" || dept === "reception" || dept === "retouch") return `qc_${dept}`;
    return "qc";
  }

  if (role === "viewer") return "viewer";

  if (role === "manager") {
    if (dept === "accounting") return "manager_accounting";
    return `manager_${dept}`;
  }

  // Canonical "sale" role — bk modelled it as staff+sale_dept
  if (role === "sale") return "staff_sale";

  if (role === "staff" && dept === "accounting") return "staff_accounting";

  if (role === "accounting" || dept === "accounting") return "accounting";

  if (role === "staff") {
    if (SALE_DEPTS.has(dept)) return `staff_${dept}`;
    if (MKT_DEPTS.has(dept)) return `staff_${dept}`;
    if (RETOUCH_DEPTS.has(dept)) return `staff_${dept}`;
    if (CSKH_DEPTS.has(dept)) return `staff_${dept}`;
    if (RECEPTION_DEPTS.has(dept)) return `staff_${dept}`;
    if (MAKEUP_DEPTS.has(dept)) return `staff_${dept}`;
    if (PHOTO_DEPTS.has(dept)) return `staff_${dept}`;
    if (ASSISTANT_DEPTS.has(dept)) return `staff_${dept}`;
  }
  return "staff";
}

/**
 * Nhân sự ngồi quầy lễ tân: nhân viên hoặc trưởng phòng của phòng lễ tân
 * (khoá `staff_reception` / `manager_reception`). Menu "Việc hằng ngày" và
 * việc mở app vào thẳng Lịch chụp chỉ áp cho nhóm này (CEO chốt 27/09/2026).
 * Trưởng cửa hàng, trưởng ca, QC phòng lễ tân KHÔNG thuộc nhóm này — ngày làm
 * việc của họ khác.
 */
export function isReceptionDesk(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  const key = accessKey(staff);
  return key === "staff_reception" || key === "manager_reception";
}

/**
 * Nhân viên kinh doanh (khoá `staff_sale` / `staff_sales`) — KHÔNG gồm trưởng
 * phòng. Bảng "Gợi ý cho khách" của nhóm này nằm ở thẻ "Khách của tôi" trên
 * trang Của tôi, không còn là thẻ của trang Sản phẩm (G5-4a, CEO chốt 09/10/2026).
 */
export function isSaleStaff(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  const key = accessKey(staff);
  return key === "staff_sale" || key === "staff_sales";
}

/**
 * Nhân sự phòng CSKH: nhân viên hoặc trưởng phòng (khoá `staff_cskh` /
 * `manager_cskh`). Menu "Việc hằng ngày" của CSKH và việc mở app vào thẳng
 * Chat khách chỉ áp cho nhóm này (CEO chốt 6a, 09/10/2026). QC phòng CSKH và
 * các vai chuỗi KHÔNG thuộc nhóm này — ngày làm việc của họ khác.
 */
export function isCskhDesk(staff: Staff | null | undefined): boolean {
  if (!staff) return false;
  const key = accessKey(staff);
  return key === "staff_cskh" || key === "manager_cskh";
}

/**
 * Trưởng ca theo chức danh HRM (QĐ 0425, CEO 19/10/2026): người mang vai
 * Nhân viên + chức danh `truong_ca` mở thêm trang vận hành của Trưởng cửa
 * hàng — trừ các trang thuộc mảng kinh doanh / tiền tổng dưới đây, và trang
 * Nhân sự (có ô lương). Khớp danh sách chặn quyền `shiftLeadBlocked` ở máy chủ.
 */
const SHIFT_LEAD_TITLE = "truong_ca";
// "qc_board" (Chất lượng nhân sự) + "branch_scores" (Điểm cơ sở) KHÔNG chặn:
// Trưởng ca chấm điểm hiệu suất như Trưởng cửa hàng (CEO chốt 1b, 20/10/2026).
// "ai_briefing": bản tin AI cảnh báo quản lý chốt theo VAI (QĐ 0428).
// "mgmt_performance" (28/10/2026): màn chấm chính người quản lý — Trưởng ca
// không có quyền `mgmtperf.read` (chặn ở `auth.shiftLeadBlocked`).
const SHIFT_LEAD_BLOCKED_PAGES = new Set(["payroll", "salary_analytics", "branch_kpi", "reports", "staff", "ai_briefing", "mgmt_performance"]);

/** Đủ để nhận ra Trưởng ca — nhiều cổng ở màn chỉ cầm vai + phòng + chức danh. */
type TitledUser = { role?: string | null; jobTitleCode?: string | null } | null | undefined;

export function isShiftLeadTitle(staff: TitledUser): boolean {
  if (!staff) return false;
  return (
    (staff.role || "").toLowerCase().trim() === "staff" &&
    (staff.jobTitleCode || "").toLowerCase().trim() === SHIFT_LEAD_TITLE
  );
}

/**
 * Vai dùng để XÉT QUYỀN ở các cổng vận hành — gương `auth.OpsRole` ở máy chủ:
 * Trưởng ca đi như Trưởng cửa hàng, mọi người khác giữ vai thật. Chỉ dùng ở
 * cổng mà máy chủ đã mở cho Trưởng ca (bảng HRS-245
 * `audit-cong-vai-truong-cua-hang.md`); cổng thuộc mảng bị chặn và mọi chỗ HIỂN
 * THỊ / ghi vai vẫn đọc vai thật.
 */
export function opsRole(staff: TitledUser): string {
  if (isShiftLeadTitle(staff)) return "branch_manager";
  return staff?.role ?? "";
}

export function hasAccess(staff: Staff | null | undefined, pageId: string): boolean {
  if (!staff) return false;
  if (pageId === "cskh_booking") {
    const role = (staff.role || "").toLowerCase().trim();
    const dept = (staff.department || "").toLowerCase().trim();
    // Vai Điều phối đã khoá (quyết định 0050) — thay bằng trưởng cửa hàng,
    // vai được máy chủ cấp customer_booking.manage.
    // Trưởng ca mang `customer_booking.manage` như Trưởng cửa hàng (QĐ 0425).
    return (
      dept === "cskh" ||
      ["admin", "director", "branch_manager", "manager"].includes(role) ||
      isShiftLeadTitle(staff)
    );
  }
  // Ca trưởng đang trong giờ ca ± 2 tiếng (CEO chốt 27/09/2026) mở được Bảng
  // suất, và từ QĐ 0259 (Câu 7a, 29/09/2026) cả Điều phối ảnh thẻ — dù vai
  // không có trang này, quyền đi theo CA, không theo vai. Máy chủ soát lại cơ
  // sở ở từng lượt gọi (orderdispatch/shiftlead_power.go, storeboard/lead.go).
  // Từ 04/11/2026 (CEO «bổ sung quyền xem đơn in về theo cơ sở cho Trưởng ca») thêm "Hàng in về cơ sở";
  // máy chủ mở đúng cơ sở của ca, chỉ xem (cskh/printops readScope).
  if ((pageId === "dispatch_board" || pageId === "store_board" || pageId === "print_ops") && isActiveShiftLead()) return true;
  const key = accessKey(staff);
  const allowed = PAGE_ACCESS[key] ?? PAGE_ACCESS.staff!;
  if (allowed.includes(pageId)) return true;
  return (
    isShiftLeadTitle(staff) &&
    !SHIFT_LEAD_BLOCKED_PAGES.has(pageId) &&
    PAGE_ACCESS.branch_manager!.includes(pageId)
  );
}
